import Vue from 'vue'
import Vuex from 'vuex'
import localArchiveService from '../services/localArchiveService'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    // Authentication
    isAuthenticated: false,
    authenticatedRestaurantId: '',
    firebaseUser: null,
    firebaseToken: null,

    // Restaurant and station selection
    restaurants: {},
    selectedRestaurantId: '',
    selectedRestaurantName: '',
    selectedStation: null,

    // Orders - MealWave orders
    orders: {},
    virtualOrders: {},
    combinedOrders: {}, // Pre-combined orders to avoid getter recreation
    menu: {},
    knownOrderIds: new Set(),

    // Table Tech Integration
    tableTechConnection: null, // { tableTechRestaurantId, status }
    tableTechOrders: {}, // Orders from Table Tech Firebase
    tableTechOrdersListener: null, // Store listener for cleanup

    // App settings
    language: 'de',
    soundEnabled: false,
    darkMode: true,
    connectionStatus: false,
    arabicEnabled: false,
    showArticleNumber: false,

    // Archive settings
    localArchiveOrders: [],
    archiveMode: 'local', // 'local' or 'cloud'

    // Firebase instance
    firebase: null
  },
  getters: {
    allOrdersIncludingVirtual: (state) => {
      // Return the pre-combined orders (includes MealWave + virtual + Table Tech)
      return state.combinedOrders;
    },
    filteredOrdersForStation: (state, getters) => {
      if (!state.selectedStation?.destinationCode) return getters.allOrdersIncludingVirtual;

      const allOrders = getters.allOrdersIncludingVirtual;
      const destinationCode = state.selectedStation.destinationCode;
      const filtered = {};

      // Build filtered object directly without creating intermediate arrays
      for (const [orderId, order] of Object.entries(allOrders)) {
        const section = order[destinationCode];
        if (section && section.items && section.items.length > 0) {
          filtered[orderId] = order;
        }
      }

      return filtered;
    },
    newOrders: (state, getters) => {
      return Object.entries(getters.filteredOrdersForStation)
        .filter(([, order]) => {
          if (!state.selectedStation?.destinationCode) return false;
          const section = order[state.selectedStation.destinationCode];
          const status = section?.status || 'New';
          return status === 'New' || status === 'Added';
        })
        .map(([id, order]) => ({ ...order, id }));
    },
    preparingOrders: (state, getters) => {
      return Object.entries(getters.filteredOrdersForStation)
        .filter(([, order]) => {
          if (!state.selectedStation?.destinationCode) return false;
          const section = order[state.selectedStation.destinationCode];
          const status = section?.status;
          return status === 'Preparing' || status === 'In Progress';
        })
        .map(([id, order]) => ({ ...order, id }));
    },
    totalOrdersCount: (state, getters) => {
      return getters.newOrders.length + getters.preparingOrders.length;
    },
    getMenuItemByArticleNumber: (state) => (articleNumber) => {
      if (!articleNumber) return null;
      
      for (const categoryKey in state.menu) {
        const category = state.menu[categoryKey];
        if (typeof category !== 'object' || categoryKey === 'category_name' || categoryKey === 'category_name_ar') {
          continue;
        }
        if (category[articleNumber] && typeof category[articleNumber] === 'object') {
          return category[articleNumber];
        }
      }
      return null;
    }
  },
  mutations: {
    SET_AUTHENTICATED(state, { isAuthenticated, restaurantId }) {
      state.isAuthenticated = isAuthenticated;
      state.authenticatedRestaurantId = restaurantId || '';
    },
    SET_FIREBASE(state, firebase) {
      state.firebase = firebase;
    },
    SET_FIREBASE_USER(state, user) {
      state.firebaseUser = user;
    },
    SET_FIREBASE_TOKEN(state, token) {
      state.firebaseToken = token;
    },
    SET_RESTAURANTS(state, restaurants) {
      state.restaurants = restaurants;
    },
    SET_SELECTED_RESTAURANT(state, { id, name }) {
      state.selectedRestaurantId = id;
      state.selectedRestaurantName = name;
    },
    SET_SELECTED_STATION(state, station) {
      state.selectedStation = station;
    },
    SET_ORDERS(state, orders) {
      // Use spread to force new reference for Vue 2 reactivity
      state.orders = { ...orders };
      // Update combined orders immediately
      Vue.set(state, 'combinedOrders', { ...state.orders, ...state.virtualOrders });
    },
    SET_MENU(state, menu) {
      state.menu = menu;
    },
    SET_CONNECTION_STATUS(state, status) {
      state.connectionStatus = status;
    },
    SET_ARABIC_ENABLED(state, enabled) {
      state.arabicEnabled = enabled;
    },
    TOGGLE_THEME(state) {
      state.darkMode = !state.darkMode;
      if (state.darkMode) {
        document.documentElement.classList.add('dark');
        document.documentElement.classList.remove('light');
      } else {
        document.documentElement.classList.remove('dark');
        document.documentElement.classList.add('light');
      }
    },
    SET_THEME(state, isDark) {
      state.darkMode = isDark;
      if (isDark) {
        document.documentElement.classList.add('dark');
        document.documentElement.classList.remove('light');
      } else {
        document.documentElement.classList.remove('dark');
        document.documentElement.classList.add('light');
      }
    },
    TOGGLE_LANGUAGE(state) {
      state.language = state.language === 'ar' ? 'de' : 'ar';
      document.documentElement.setAttribute('lang', state.language);
    },
    TOGGLE_SOUND(state) {
      state.soundEnabled = !state.soundEnabled;
    },
    TOGGLE_ARTICLE_NUMBER(state) {
      state.showArticleNumber = !state.showArticleNumber;
    },
    ADD_KNOWN_ORDER_ID(state, orderId) {
      state.knownOrderIds.add(orderId);
    },
    ADD_VIRTUAL_ORDER(state, { id, order }) {
      Vue.set(state.virtualOrders, id, order);
      // Update combined orders
      Vue.set(state.combinedOrders, id, order);
    },
    REMOVE_VIRTUAL_ORDER(state, id) {
      Vue.delete(state.virtualOrders, id);
      // Update combined orders
      Vue.delete(state.combinedOrders, id);
    },
    ADD_ORDER(state, { id, order }) {
      Vue.set(state.orders, id, order);
      // Also update combined orders
      Vue.set(state.combinedOrders, id, order);
    },
    UPDATE_ORDER(state, { id, order }) {
      Vue.set(state.orders, id, order);
      // Also update combined orders
      Vue.set(state.combinedOrders, id, order);
    },
    CLEAR_VIRTUAL_ORDERS(state) {
      state.virtualOrders = {};
      // Rebuild combined orders without virtual orders
      Vue.set(state, 'combinedOrders', { ...state.orders });
    },
    BATCH_ADD_VIRTUAL_ORDERS(state, virtualOrders) {
      // Batch add multiple virtual orders at once for performance
      virtualOrders.forEach(({ id, order }) => {
        Vue.set(state.virtualOrders, id, order);
        Vue.set(state.combinedOrders, id, order);
      });
    },
    BATCH_UPDATE_ALL_ORDERS(state, { orders, virtualOrders }) {
      // Update all orders in one atomic operation to minimize reactivity cycles
      // First, set the main orders
      state.orders = { ...orders };

      // Then reset virtual orders
      state.virtualOrders = {};

      // Add new virtual orders
      virtualOrders.forEach(({ id, order }) => {
        Vue.set(state.virtualOrders, id, order);
      });

      // Finally, update combined orders in one shot
      const combined = { ...state.orders };
      Object.entries(state.virtualOrders).forEach(([id, order]) => {
        combined[id] = order;
      });

      // Use Vue.set to ensure reactivity on the whole object
      Vue.set(state, 'combinedOrders', combined);
    },
    SET_LOCAL_ARCHIVE_ORDERS(state, orders) {
      state.localArchiveOrders = orders;
    },
    ADD_TO_LOCAL_ARCHIVE(state, order) {
      state.localArchiveOrders.unshift(order);
    },
    SET_ARCHIVE_MODE(state, mode) {
      state.archiveMode = mode;
    },
    // Table Tech Integration mutations
    SET_TABLE_TECH_CONNECTION(state, connection) {
      state.tableTechConnection = connection;
    },
    SET_TABLE_TECH_ORDERS(state, orders) {
      state.tableTechOrders = { ...orders };
      // Also update combinedOrders to include Table Tech orders for reactivity
      Vue.set(state, 'combinedOrders', { ...state.orders, ...state.virtualOrders, ...orders });
    },
    ADD_TABLE_TECH_ORDER(state, { id, order }) {
      Vue.set(state.tableTechOrders, id, order);
      // Also add to combinedOrders
      Vue.set(state.combinedOrders, id, order);
    },
    UPDATE_TABLE_TECH_ORDER(state, { id, order }) {
      Vue.set(state.tableTechOrders, id, order);
      // Also update in combinedOrders
      Vue.set(state.combinedOrders, id, order);
    },
    REMOVE_TABLE_TECH_ORDER(state, id) {
      Vue.delete(state.tableTechOrders, id);
      // Also remove from combinedOrders
      Vue.delete(state.combinedOrders, id);
    },
    CLEAR_TABLE_TECH_ORDERS(state) {
      // Remove all Table Tech orders from combinedOrders
      Object.keys(state.tableTechOrders).forEach(id => {
        Vue.delete(state.combinedOrders, id);
      });
      state.tableTechOrders = {};
    },
    SET_TABLE_TECH_LISTENER(state, listener) {
      state.tableTechOrdersListener = listener;
    }
  },
  actions: {
    setFirebase({ commit }, firebase) {
      commit('SET_FIREBASE', firebase);
    },
    async loadRestaurants({ commit, state }) {
      if (!state.firebase) return;
      
      try {
        const { ref, onValue } = await import('firebase/database');
        const db = state.firebase.database();
        
        return new Promise((resolve, reject) => {
          const restaurantsRef = ref(db, 'restaurants');
          onValue(restaurantsRef, (snapshot) => {
            if (snapshot.exists()) {
              commit('SET_RESTAURANTS', snapshot.val());
              resolve();
            } else {
              resolve();
            }
          }, (error) => {
            console.error('Error loading restaurants:', error);
            reject(error);
          }, { onlyOnce: true });
        });
      } catch (error) {
        console.error('Error loading restaurants:', error);
        throw error;
      }
    },
    setSelectedRestaurant({ commit }, restaurant) {
      commit('SET_SELECTED_RESTAURANT', restaurant);
      // Save to localStorage
      localStorage.setItem('restaurantId', restaurant.id);
      localStorage.setItem('restaurantName', restaurant.name);
    },
    setSelectedStation({ commit }, station) {
      commit('SET_SELECTED_STATION', station);
      // Save to localStorage
      localStorage.setItem('stationId', station.id);
      localStorage.setItem('stationLetter', station.destinationCode);
    },
    updateOrders({ commit }, orders) {
      commit('SET_ORDERS', orders);
    },
    async loadMenu({ commit, state }) {
      if (!state.firebase || !state.selectedRestaurantId) return;
      
      try {
        const { ref, get } = await import('firebase/database');
        const db = state.firebase.database();
        const menuRef = ref(db, `restaurants/${state.selectedRestaurantId}/menu`);
        const snapshot = await get(menuRef);
        
        if (snapshot.exists()) {
          commit('SET_MENU', snapshot.val());
        }
      } catch (error) {
        console.error('Error loading menu:', error);
      }
    },
    setConnectionStatus({ commit }, status) {
      commit('SET_CONNECTION_STATUS', status);
    },
    async listenToArabicSetting({ commit, state }) {
      if (!state.firebase || !state.selectedRestaurantId) return;

      try {
        const { ref, onValue } = await import('firebase/database');
        const db = state.firebase.database();

        const settingsRef = ref(db, `restaurants/${state.selectedRestaurantId}/settings/arabicEnabled`);
        onValue(settingsRef, (snapshot) => {
          const enabled = snapshot.val() === true;
          commit('SET_ARABIC_ENABLED', enabled);
          console.log('📱 [STORE] Arabic enabled:', enabled);
        });
      } catch (error) {
        console.error('Error listening to Arabic setting:', error);
      }
    },
    toggleTheme({ commit, state }) {
      commit('TOGGLE_THEME');
      localStorage.setItem('darkMode', state.darkMode);
    },
    toggleLanguage({ commit }) {
      commit('TOGGLE_LANGUAGE');
      localStorage.setItem('language', this.state.language);
    },
    toggleSound({ commit }) {
      commit('TOGGLE_SOUND');
      localStorage.setItem('soundEnabled', this.state.soundEnabled);
    },
    toggleArticleNumber({ commit }) {
      commit('TOGGLE_ARTICLE_NUMBER');
      localStorage.setItem('showArticleNumber', this.state.showArticleNumber);
    },
    async startPreparingOrder({ state, commit, dispatch }, orderId) {
      if (!state.firebase || !state.selectedRestaurantId || !state.selectedStation?.destinationCode) return;

      const destinationCode = state.selectedStation.destinationCode;

      // Check if this is a Table Tech order (prefixed with tt_)
      if (orderId.startsWith('tt_')) {
        const realOrderId = orderId.replace('tt_', '');
        console.log('📱 [STORE] Updating Table Tech order:', realOrderId);

        // Optimistic update - update local state immediately via mutation
        const order = state.tableTechOrders[orderId];
        if (order && order[destinationCode]) {
          const updatedOrder = {
            ...order,
            [destinationCode]: {
              ...order[destinationCode],
              status: 'Preparing',
              preparedAt: new Date().toISOString()
            }
          };
          commit('UPDATE_TABLE_TECH_ORDER', { id: orderId, order: updatedOrder });
        }

        // Then update Firebase
        await dispatch('updateTableTechOrderStatus', {
          orderId: realOrderId,
          destinationCode,
          status: 'Preparing'
        });
        return;
      }

      // MealWave order - update in MealWave Firebase
      try {
        const { ref, update } = await import('firebase/database');
        const db = state.firebase.database();

        const preparedAt = new Date().toISOString();

        // Update destination section status (K or D)
        const sectionPath = `restaurants/${state.selectedRestaurantId}/live_orders/${orderId}/${destinationCode}`;
        const sectionRef = ref(db, sectionPath);

        const sectionUpdates = { status: 'Preparing' };
        if (destinationCode === 'K' || destinationCode === 'D') {
          sectionUpdates.preparedAt = preparedAt;
        }

        // Also update the overall order status
        const orderRef = ref(db, `restaurants/${state.selectedRestaurantId}/live_orders/${orderId}`);
        const orderUpdates = { status: 'preparing' };

        console.log('📱 [STORE] Updating order status:', sectionPath, 'with:', sectionUpdates);
        console.log('📱 [STORE] Also updating overall order status to: preparing');

        await Promise.all([
          update(sectionRef, sectionUpdates),
          update(orderRef, orderUpdates)
        ]);

        console.log('📱 [STORE] ✅ Successfully updated order status to Preparing');

      } catch (error) {
        console.error('Error starting order preparation:', error);
      }
    },
    async completeOrder({ state, commit, dispatch }, orderId) {
      if (!state.firebase || !state.selectedRestaurantId || !state.selectedStation?.destinationCode) return;

      const destinationCode = state.selectedStation.destinationCode;
      const completedAt = new Date().toISOString();

      // Check if this is a Table Tech order (prefixed with tt_)
      if (orderId.startsWith('tt_')) {
        const realOrderId = orderId.replace('tt_', '');
        console.log('📱 [STORE] Completing Table Tech order:', realOrderId);

        // Save order before removing it (for archive)
        const order = state.tableTechOrders[orderId];

        // Optimistic update - remove from local state immediately via mutation
        if (order) {
          commit('REMOVE_TABLE_TECH_ORDER', orderId);
        }

        // Then update Firebase
        await dispatch('updateTableTechOrderStatus', {
          orderId: realOrderId,
          destinationCode,
          status: 'Completed'
        });
        if (order) {
          const archivedOrder = {
            ...order,
            id: orderId,
            restaurantId: state.selectedRestaurantId,
            stationId: state.selectedStation.id,
            stationName: state.selectedStation.name,
            completedAt: completedAt,
            _source: 'tabletech'
          };
          const savedOrder = localArchiveService.saveOrder(archivedOrder);
          if (savedOrder) {
            commit('ADD_TO_LOCAL_ARCHIVE', savedOrder);
            console.log('📱 Table Tech order saved to local archive:', orderId);
          }
        }
        return;
      }

      // MealWave order - update in MealWave Firebase
      try {
        const { ref, update, get } = await import('firebase/database');
        const db = state.firebase.database();

        // Get the order data before marking it complete
        const order = state.orders[orderId] || state.virtualOrders[orderId];

        // Update destination section status (K or D)
        const sectionPath = `restaurants/${state.selectedRestaurantId}/live_orders/${orderId}/${destinationCode}`;
        const sectionRef = ref(db, sectionPath);

        const sectionUpdates = { status: 'Completed' };
        if (destinationCode === 'K' || destinationCode === 'D') {
          sectionUpdates.completedAt = completedAt;
        }

        console.log('📱 [STORE] Completing order:', sectionPath, 'with:', sectionUpdates);
        await update(sectionRef, sectionUpdates);

        // Check if all sections are completed to update overall status
        const orderRef = ref(db, `restaurants/${state.selectedRestaurantId}/live_orders/${orderId}`);
        const orderSnapshot = await get(orderRef);

        if (orderSnapshot.exists()) {
          const orderData = orderSnapshot.val();

          // Check if K and D sections are both completed (or don't exist)
          const kCompleted = !orderData.K || orderData.K.status === 'Completed';
          const dCompleted = !orderData.D || orderData.D.status === 'Completed';

          if (kCompleted && dCompleted) {
            // All sections completed - update overall status to completed
            await update(orderRef, { status: 'completed' });
            console.log('📱 [STORE] ✅ All sections completed - overall status set to completed');
          }
        }

        console.log('📱 [STORE] ✅ Successfully updated order status to Completed');

        // Save to local archive
        if (order) {
          const archivedOrder = {
            ...order,
            id: orderId,
            restaurantId: state.selectedRestaurantId,
            stationId: state.selectedStation.id,
            stationName: state.selectedStation.name,
            completedAt: completedAt
          };

          const savedOrder = localArchiveService.saveOrder(archivedOrder);
          if (savedOrder) {
            commit('ADD_TO_LOCAL_ARCHIVE', savedOrder);
            console.log('📱 Order saved to local archive:', orderId);
          }
        }

      } catch (error) {
        console.error('Error completing order:', error);
      }
    },

    async calculateAndStoreDelay({ state }, { orderId, destinationCode, completedAt, orderTimestamp }) {
      if (!state.firebase || !state.selectedRestaurantId) return;

      try {
        const { ref, get, update } = await import('firebase/database');
        const db = state.firebase.database();

        // Calculate delay in milliseconds
        const orderTime = new Date(orderTimestamp).getTime();
        const completedTime = new Date(completedAt).getTime();
        const delayMs = completedTime - orderTime;
        const delayMinutes = Math.floor(delayMs / (1000 * 60)); // Convert to minutes

        const DELAY_THRESHOLD_MINUTES = 40;
        const isDelayed = delayMinutes > DELAY_THRESHOLD_MINUTES;

        console.log(`⏱️ [DELAY CALC] Order ${orderId} - ${destinationCode}: ${delayMinutes} minutes`);

        if (isDelayed) {
          console.log(`🚨 [DELAY DETECTED] Order ${orderId} - ${destinationCode} took ${delayMinutes} minutes (>${DELAY_THRESHOLD_MINUTES} min threshold)`);
        }

        // Get existing delayInfo from order in MealWave live_orders
        const delayInfoRef = ref(db, `restaurants/${state.selectedRestaurantId}/live_orders/${orderId}/delayInfo`);
        const snapshot = await get(delayInfoRef);
        const existingDelayInfo = snapshot.val() || {};

        // Update delay info for this destination
        const delays = existingDelayInfo.delays || {};
        delays[destinationCode] = {
          delayMinutes,
          isDelayed,
          completedAt
        };

        // Check if ANY destination is delayed
        const anyDelayed = Object.values(delays).some(d => d.isDelayed);
        const delayedSections = Object.entries(delays)
          .filter(([, d]) => d.isDelayed)
          .map(([section]) => section);

        // Find the maximum delay
        const maxDelayMinutes = Math.max(...Object.values(delays).map(d => d.delayMinutes));

        // Update delayInfo
        const updatedDelayInfo = {
          isDelayed: anyDelayed,
          maxDelayMinutes,
          delayedSections,
          delays,
          lastUpdated: new Date().toISOString()
        };

        await update(delayInfoRef, updatedDelayInfo);

        console.log(`✅ [DELAY INFO SAVED] Order ${orderId}:`, updatedDelayInfo);

      } catch (error) {
        console.error('Error calculating and storing delay:', error);
      }
    },

    async cancelOrder({ state }, { orderId, reason = '' }) {
      if (!state.firebase || !state.selectedRestaurantId || !state.selectedStation?.destinationCode) return;

      try {
        const { ref, set, get, serverTimestamp } = await import('firebase/database');
        const db = state.firebase.database();
        const destinationCode = state.selectedStation.destinationCode;

        // Update this station's status to Cancelled in MealWave live_orders
        const statusRef = ref(db, `restaurants/${state.selectedRestaurantId}/live_orders/${orderId}/${destinationCode}/status`);
        await set(statusRef, 'Cancelled');
        console.log(`📱 [STORE] Marked order station ${destinationCode} as cancelled:`, orderId);

        // Check if all relevant stations are cancelled
        const orderRef = ref(db, `restaurants/${state.selectedRestaurantId}/live_orders/${orderId}`);
        const orderSnapshot = await get(orderRef);
        if (!orderSnapshot.exists()) return;

        const orderData = orderSnapshot.val();

        // Check if K and D sections exist and their status
        const isFullyCancelled = this.checkIfOrderFullyCancelled(orderData);

        // If all relevant sections are cancelled, add the full cancellation metadata
        if (isFullyCancelled) {
          console.log('📱 [STORE] All stations cancelled, marking order as fully cancelled');

          // Add cancellation metadata
          const metadataRef = ref(db, `restaurants/${state.selectedRestaurantId}/live_orders/${orderId}/cancellation`);
          await set(metadataRef, {
            timestamp: serverTimestamp(),
            reason: reason || 'All stations cancelled',
            stationCode: destinationCode,
            stationName: state.selectedStation.name || '',
            cancelledBy: state.selectedStation.name || 'Staff'
          });
        }
      } catch (error) {
        console.error('Error cancelling order:', error);
      }
    },
    
    // Helper method to check if an order is fully cancelled across all relevant stations
    checkIfOrderFullyCancelled(orderData) {
      // The stations to check (K and D are the important ones)
      const stationsToCheck = ['K', 'D'];
      
      // Check if each station exists and if so, whether it's cancelled
      let allRelevantStationsCancelled = true;
      
      for (const station of stationsToCheck) {
        // Skip if this station doesn't exist in the order
        if (!orderData[station]) continue;
        
        // If this station has items but is not cancelled, the order is not fully cancelled
        if (orderData[station].items && orderData[station].items.length > 0) {
          if (orderData[station].status !== 'Cancelled') {
            allRelevantStationsCancelled = false;
            break;
          }
        }
      }
      
      return allRelevantStationsCancelled;
    },
    
    async cancelOrderItem({ state, dispatch }, { orderId, itemIndex, reason = '' }) {
      if (!state.firebase || !state.selectedRestaurantId || !state.selectedStation?.destinationCode) return;

      try {
        const { ref, get, set } = await import('firebase/database');
        const db = state.firebase.database();
        const destinationCode = state.selectedStation.destinationCode;

        // Get current items array from MealWave live_orders
        const itemsRef = ref(db, `restaurants/${state.selectedRestaurantId}/live_orders/${orderId}/${destinationCode}/items`);
        const snapshot = await get(itemsRef);

        if (snapshot.exists()) {
          const items = snapshot.val();
          if (Array.isArray(items) && itemIndex >= 0 && itemIndex < items.length) {
            // Mark item as cancelled instead of removing it
            const updatedItems = [...items];
            updatedItems[itemIndex] = {
              ...updatedItems[itemIndex],
              cancelled: true,
              cancellationReason: reason || 'Cancelled by staff',
              cancellationTimestamp: new Date().toISOString(),
              cancelledBy: state.selectedStation.name || 'Staff'
            };

            // Update the items array
            await set(itemsRef, updatedItems);
            console.log('📱 [STORE] Cancelled item in order:', { orderId, itemIndex });

            // Check if all items for this station are now cancelled
            const allItemsCancelled = updatedItems.every(item => item.cancelled === true);
            if (allItemsCancelled) {
              console.log('📱 [STORE] All items cancelled for this station, updating station status');
              // Update the station status to Cancelled
              dispatch('cancelOrder', {
                orderId: orderId,
                reason: 'All items cancelled'
              });
            }
          }
        }

      } catch (error) {
        console.error('Error cancelling order item:', error);
      }
    },
    setAuthUser({ commit }, { user, token, restaurantId }) {
      commit('SET_FIREBASE_USER', user);
      commit('SET_FIREBASE_TOKEN', token);
      commit('SET_AUTHENTICATED', { isAuthenticated: true, restaurantId });
      
      // Store auth state
      localStorage.setItem('authenticated', 'true');
      localStorage.setItem('authenticatedRestaurantId', restaurantId);
    },
    
    clearAuthUser({ commit }) {
      commit('SET_FIREBASE_USER', null);
      commit('SET_FIREBASE_TOKEN', null);
      commit('SET_AUTHENTICATED', { isAuthenticated: false, restaurantId: '' });
      
      // Clear stored auth state
      localStorage.removeItem('authenticated');
      localStorage.removeItem('authenticatedRestaurantId');
    },
    
    login({ commit }, { restaurantId, user, token }) {
      // Set authentication state with Firebase user
      commit('SET_FIREBASE_USER', user);
      commit('SET_FIREBASE_TOKEN', token);
      commit('SET_AUTHENTICATED', { isAuthenticated: true, restaurantId });
      
      // Store in localStorage
      localStorage.setItem('authenticated', 'true');
      localStorage.setItem('authenticatedRestaurantId', restaurantId);
    },
    
    async logout({ commit, state }) {
      try {
        // Sign out from Firebase
        if (state.firebase?.auth) {
          const { signOut } = await import('firebase/auth');
          await signOut(state.firebase.auth());
        }
      } catch (error) {
        console.error('Error signing out:', error);
      }
      
      // Clear authentication
      commit('SET_FIREBASE_USER', null);
      commit('SET_FIREBASE_TOKEN', null);
      commit('SET_AUTHENTICATED', { isAuthenticated: false, restaurantId: '' });
      
      // Clear session data
      localStorage.removeItem('authenticated');
      localStorage.removeItem('authenticatedRestaurantId');
      localStorage.removeItem('restaurantId');
      localStorage.removeItem('restaurantName');
      localStorage.removeItem('stationId');
      localStorage.removeItem('stationLetter');
      
      // Reset state
      commit('SET_SELECTED_RESTAURANT', { id: '', name: '' });
      commit('SET_SELECTED_STATION', null);
      commit('SET_ORDERS', {});
    },
    loadLocalArchive({ commit }) {
      const orders = localArchiveService.init();
      commit('SET_LOCAL_ARCHIVE_ORDERS', orders);
      return orders;
    },
    
    toggleArchiveMode({ commit, state }) {
      const newMode = state.archiveMode === 'local' ? 'cloud' : 'local';
      commit('SET_ARCHIVE_MODE', newMode);
      localStorage.setItem('archiveMode', newMode);
      return newMode;
    },
    
    searchLocalArchive(context, query) {
      return localArchiveService.searchOrders(query);
    },
    
    clearLocalArchive({ commit }) {
      const success = localArchiveService.clearAll();
      if (success) {
        commit('SET_LOCAL_ARCHIVE_ORDERS', []);
      }
      return success;
    },
    
    exportLocalArchive() {
      localArchiveService.exportArchive();
    },
    
    async importLocalArchive({ commit }, file) {
      try {
        const count = await localArchiveService.importArchive(file);
        const orders = localArchiveService.getAllOrders();
        commit('SET_LOCAL_ARCHIVE_ORDERS', orders);
        return count;
      } catch (error) {
        console.error('Error importing archive:', error);
        throw error;
      }
    },
    
    initializeFromLocalStorage({ commit, dispatch }) {
      // Load authentication state
      const isAuthenticated = localStorage.getItem('authenticated') === 'true';
      const authenticatedRestaurantId = localStorage.getItem('authenticatedRestaurantId');

      if (isAuthenticated && authenticatedRestaurantId) {
        commit('SET_AUTHENTICATED', { isAuthenticated: true, restaurantId: authenticatedRestaurantId });
      }

      // Load user preferences from localStorage
      const savedDarkMode = localStorage.getItem('darkMode');
      const darkMode = savedDarkMode !== null ? savedDarkMode === 'true' : true; // Default to dark mode
      const language = localStorage.getItem('language') || 'de';
      const soundEnabled = localStorage.getItem('soundEnabled') === 'true';
      const showArticleNumber = localStorage.getItem('showArticleNumber') === 'true';
      const archiveMode = localStorage.getItem('archiveMode') || 'local';

      // Apply saved settings
      commit('SET_THEME', darkMode);
      if (language === 'ar') commit('TOGGLE_LANGUAGE');
      if (soundEnabled) commit('TOGGLE_SOUND');
      if (showArticleNumber) commit('TOGGLE_ARTICLE_NUMBER');
      commit('SET_ARCHIVE_MODE', archiveMode);
      
      // Load local archive
      dispatch('loadLocalArchive');
      
      // Load saved session
      const restaurantId = localStorage.getItem('restaurantId');
      const restaurantName = localStorage.getItem('restaurantName');
      const stationId = localStorage.getItem('stationId');
      const stationLetter = localStorage.getItem('stationLetter');
      
      if (restaurantId) {
        commit('SET_SELECTED_RESTAURANT', { id: restaurantId, name: restaurantName });
      }
      if (stationId) {
        commit('SET_SELECTED_STATION', {
          id: stationId,
          destinationCode: stationLetter,
          name: stationId // Will be updated when stations load
        });
      }
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TABLE TECH INTEGRATION ACTIONS
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Check if this MealWave restaurant has any approved Table Tech connections
     * Reads from Firestore: restaurants/{mealwaveId}/pos_connections
     */
    async checkTableTechConnections({ commit, state }) {
      if (!state.firebase || !state.selectedRestaurantId) {
        console.log('🔗 [TABLE TECH] No firebase or restaurant ID, skipping connection check');
        return;
      }

      try {
        const { collection, getDocs, query, where } = await import('firebase/firestore');
        const firestore = state.firebase.firestore();

        console.log('🔗 [TABLE TECH] Checking for connections for restaurant:', state.selectedRestaurantId);

        // Query pos_connections subcollection for approved connections
        const connectionsRef = collection(firestore, 'restaurants', state.selectedRestaurantId, 'pos_connections');
        const q = query(connectionsRef, where('status', '==', 'approved'));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          // Get the first approved connection
          const connectionDoc = snapshot.docs[0];
          const connectionData = connectionDoc.data();

          console.log('🔗 [TABLE TECH] ✅ Found approved connection:', connectionDoc.id);
          console.log('🔗 [TABLE TECH] Connection data:', connectionData);

          commit('SET_TABLE_TECH_CONNECTION', {
            tableTechRestaurantId: connectionDoc.id,
            tableTechRestaurantName: connectionData.tableTechRestaurantName,
            status: 'approved',
            connectedAt: connectionData.approvedAt
          });

          return connectionDoc.id; // Return Table Tech restaurant ID
        } else {
          console.log('🔗 [TABLE TECH] No approved connections found');
          commit('SET_TABLE_TECH_CONNECTION', null);
          return null;
        }

      } catch (error) {
        console.error('🔗 [TABLE TECH] Error checking connections:', error);
        commit('SET_TABLE_TECH_CONNECTION', null);
        return null;
      }
    },

    /**
     * Start listening to Table Tech orders
     * Reads from Table Tech Firebase: restaurants/{tableTechId}/orders
     */
    async listenToTableTechOrders({ commit, state, dispatch }) {
      // First check for connections
      const tableTechRestaurantId = await dispatch('checkTableTechConnections');

      if (!tableTechRestaurantId) {
        console.log('🔗 [TABLE TECH] No connection, not listening to orders');
        return;
      }

      if (!state.firebase?.tableTechDatabase) {
        console.error('🔗 [TABLE TECH] Table Tech database not initialized');
        return;
      }

      try {
        const { ref, onValue } = await import('firebase/database');
        const tableTechDb = state.firebase.tableTechDatabase();

        // Clean up existing listener
        if (state.tableTechOrdersListener) {
          console.log('🔗 [TABLE TECH] Cleaning up existing listener');
          state.tableTechOrdersListener();
        }

        // Table Tech uses "orders" path (not "live_orders")
        const ordersPath = `restaurants/${tableTechRestaurantId}/orders`;
        console.log('🔗 [TABLE TECH] Starting listener at:', ordersPath);

        const ordersRef = ref(tableTechDb, ordersPath);

        const unsubscribe = onValue(ordersRef, (snapshot) => {
          const ordersData = snapshot.val() || {};
          const currentStation = state.selectedStation?.destinationCode;

          // Transform orders to add source identifier
          const transformedOrders = {};
          for (const [orderId, order] of Object.entries(ordersData)) {
            // Skip placeholder entries
            if (!order || order === true || order.placeholder) continue;

            // Add the main order
            transformedOrders[`tt_${orderId}`] = {
              ...order,
              id: orderId,
              _source: 'tabletech',
              _tableTechRestaurantId: tableTechRestaurantId,
            };

            // Create virtual orders for each addition
            if (order.additions && currentStation) {
              Object.entries(order.additions).forEach(([additionId, addition]) => {
                // Only create virtual order if this addition has items for current station
                if (addition[currentStation]?.items?.length > 0) {
                  const virtualOrderId = `tt_${orderId}_${additionId}`;

                  transformedOrders[virtualOrderId] = {
                    [currentStation]: addition[currentStation],
                    id: virtualOrderId,
                    parentOrderId: orderId,
                    tableNumber: order.tableNumber,
                    orderNumber: order.orderNumber,
                    waiter: addition.addedBy || order.waiter,
                    timestamp: addition.timestamp || order.timestamp,
                    orderType: order.orderType,
                    table: order.table,
                    isAddition: true,
                    isVirtualOrder: true,
                    _source: 'tabletech',
                    _tableTechRestaurantId: tableTechRestaurantId,
                  };
                }
              });
            }
          }

          console.log('🔗 [TABLE TECH] Orders received:', Object.keys(transformedOrders).length);
          commit('SET_TABLE_TECH_ORDERS', transformedOrders);

        }, (error) => {
          console.error('🔗 [TABLE TECH] Error listening to orders:', error);
        });

        commit('SET_TABLE_TECH_LISTENER', unsubscribe);
        console.log('🔗 [TABLE TECH] ✅ Listener started successfully');

      } catch (error) {
        console.error('🔗 [TABLE TECH] Error starting orders listener:', error);
      }
    },

    /**
     * Stop listening to Table Tech orders
     */
    stopTableTechOrdersListener({ commit, state }) {
      if (state.tableTechOrdersListener) {
        console.log('🔗 [TABLE TECH] Stopping orders listener');
        state.tableTechOrdersListener();
        commit('SET_TABLE_TECH_LISTENER', null);
        commit('CLEAR_TABLE_TECH_ORDERS');
      }
    },

    /**
     * Update order status in Table Tech Firebase
     * Used when completing/preparing orders that came from Table Tech
     */
    async updateTableTechOrderStatus({ state }, { orderId, destinationCode, status }) {
      if (!state.firebase?.tableTechDatabase || !state.tableTechConnection) {
        console.error('🔗 [TABLE TECH] Cannot update - not connected');
        return;
      }

      try {
        const { ref, update } = await import('firebase/database');
        const tableTechDb = state.firebase.tableTechDatabase();
        const tableTechRestaurantId = state.tableTechConnection.tableTechRestaurantId;

        // Check if this is an addition order (contains 'add_' in the ID)
        const orderParts = orderId.split('_');
        const addIndex = orderParts.findIndex(part => part === 'add');

        let sectionPath;
        if (addIndex !== -1 && addIndex < orderParts.length - 1) {
          // This is an addition order: parentOrderId_add_timestamp
          const parentOrderId = orderParts.slice(0, addIndex).join('_');
          const additionId = orderParts.slice(addIndex).join('_');
          sectionPath = `restaurants/${tableTechRestaurantId}/orders/${parentOrderId}/additions/${additionId}/${destinationCode}`;
          console.log('🔗 [TABLE TECH] Updating addition order:', { parentOrderId, additionId, destinationCode });
        } else {
          // Regular order
          sectionPath = `restaurants/${tableTechRestaurantId}/orders/${orderId}/${destinationCode}`;
        }

        const sectionRef = ref(tableTechDb, sectionPath);

        const updates = { status };
        if (status === 'Preparing') {
          updates.preparedAt = new Date().toISOString();
        } else if (status === 'Completed') {
          updates.completedAt = new Date().toISOString();
        }

        await update(sectionRef, updates);
        console.log('🔗 [TABLE TECH] ✅ Order status updated:', orderId, destinationCode, status);

      } catch (error) {
        console.error('🔗 [TABLE TECH] Error updating order status:', error);
      }
    }
  }
})